import React, { useEffect, useState } from "react";
import { formatDateTime, useDecodedTokenResult } from "../../lib/utils";
import { db } from "../../config/firebaseConfig";
import {
  ref,
  onValue,
  orderByChild,
  startAt,
  endAt,
  query,
} from "firebase/database";
import { Link } from "react-router-dom";

interface Chat {
  id: string;
  customerId: string;
  customerName?: string;
  readByVendor: boolean;
  readByCustomer: boolean;
  lastMessage?: {
    text: string;
    timestamp: number;
    sender: "vendor" | "customer";
  };
}

interface UserListProps {
  onChatSelect: (customerId: string) => void;
  selectedCustomerId: string | null;
}

const UserList: React.FC<UserListProps> = ({
  onChatSelect,
  selectedCustomerId,
}) => {
  const { vendorId } = useDecodedTokenResult();
  const [chats, setChats] = useState<Chat[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!vendorId) return;

    const chatsRef = ref(db, "chats");
    const chatsQuery = searchTerm
      ? query(
          ref(db, "chats"),
          orderByChild("customerName"),
          startAt(searchTerm.toLowerCase()),
          endAt(searchTerm.toLowerCase() + "\uf8ff")
        )
      : ref(db, "chats");

    const unsubscribe = onValue(
      chatsQuery,
      (snapshot) => {
        if (snapshot.exists()) {
          const chatsData = snapshot.val();
          const filteredChats: Chat[] = [];

          Object.entries(chatsData).forEach(
            ([chatId, chatData]: [string, any]) => {
              if (chatId.startsWith(`${vendorId}_`)) {
                const customerId = chatId.split("_")[1];
                const messages = chatData.messages;
                const customerName = chatData.customerName;

                let lastMessage;
                if (messages) {
                  const messageArray = Object.values(messages);
                  lastMessage = messageArray[messageArray.length - 1];
                }

                filteredChats.push({
                  id: chatId,
                  customerId,
                  customerName,
                  lastMessage: lastMessage as {
                    text: string;
                    timestamp: number;
                    sender: "vendor" | "customer";
                  },
                  readByVendor: false,
                  readByCustomer: false,
                });
              }
            }
          );

          // Sort chats by last message timestamp (most recent first)
          filteredChats.sort((a, b) => {
            const timeA = a.lastMessage?.timestamp || 0;
            const timeB = b.lastMessage?.timestamp || 0;
            return timeB - timeA;
          });

          setChats(filteredChats);
        } else {
          setChats([]);
        }
        setLoading(false);
      },
      (error) => {
        setError("Failed to load chats: " + error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [vendorId, searchTerm]);

  const ChatSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white border rounded-xl p-4 animate-pulse">
          <div className="flex items-start">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1 ml-2">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              <div className="h-2 bg-gray-200 rounded w-1/4 mt-2"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="p-3 border-b bg-white shadow-sm">
        <h2 className="text-xl font-semibold text-gray-800">
          Customer Conversations
        </h2>
        <div className="mt-2">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-gray-300 focus:outline-customGreenColor focus:border-customGreenColor transition-colors text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <ChatSkeleton />
        ) : (
          <div>
            {chats.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No conversations found</p>
              </div>
            ) : (
              chats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => onChatSelect(chat.customerId)}
                  className={`cursor-pointer px-3 py-2 hover:bg-gray-50 ${
                    selectedCustomerId === chat.customerId
                      ? "bg-green-50"
                      : "bg-white"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-customGreenColor flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium">
                        {(chat.customerName || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {chat.customerName ||
                            `Customer #${chat.customerId.slice(0, 8)}`}
                        </h3>
                        {chat.lastMessage && (
                          <span className="text-xs text-gray-500 ml-2">
                            {formatDateTime(
                              chat.lastMessage.timestamp,
                              "h:mm A"
                            )}
                          </span>
                        )}
                      </div>
                      {chat.lastMessage && (
                        <p className="text-sm text-gray-500 truncate">
                          {chat.lastMessage.sender === "vendor" ? "You: " : ""}
                          {chat.lastMessage.text}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
